import React, { useState, useEffect } from "react";
import { Alert, Box, CircularProgress, Snackbar, Typography } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import CryptoJS from "crypto-js";
import Login from "../../Sections/Auth/Login";
import ForgotPassword from "../../Sections/Auth/ForgotPassword";
import VerifyOtp from "../../Sections/Auth/VerifyOtp";
import ResetPassword from "../../Sections/Auth/ResetPassword";
import IpBlockedPage from "../../components/Common/IpBlocked";

import { getCurrentUserIpAction } from "../../Actions/whiteListpsAction";
import { getPublicSettingsAction } from "../../Actions/SettingAction";
import { useUser } from "../../components/ContextAPI/ProfileContext";
import { CRYPTO_TOKEN_SECRET_KEY } from "../../config";
import useDeviceInfo from "../../hooks/useDeviceInfo";
import logo from "../../Assets/icon.png";
import netcluesLogo from "../../Assets/netclues-logo.gif";
import NetcluesBrandLogo from "../../../src/Assets/svg/favicon.svg";

const getAuthToken = () => {
	const token = localStorage.getItem("token");
	if (!token) return null;
	return CryptoJS.AES.decrypt(token, CRYPTO_TOKEN_SECRET_KEY).toString(CryptoJS.enc.Utf8);
};

const getUserData = () => {
	const userData = localStorage.getItem("userData");
	if (!userData) return null;
	return CryptoJS.AES.decrypt(userData, CRYPTO_TOKEN_SECRET_KEY).toString(CryptoJS.enc.Utf8);
};

const SCREENS = {
	LOGIN: "LOGIN",
	FORGOT_PASSWORD: "FORGOT_PASSWORD",
	VERIFY_OTP: "VERIFY_OTP",
	RESET_PASSWORD: "RESET_PASSWORD",
	CHANGE_PASSWORD: "CHANGE_PASSWORD",
	SET_PASSWORD: "SET_PASSWORD",
};

const LoginComponent = () => {
	const { browser } = useDeviceInfo();
	const navigate = useNavigate();
	const { updateUserData } = useUser();

	const [currentScreen, setCurrentScreen] = useState(SCREENS.LOGIN);

	const [email, setEmail] = useState("");
	const [accessToken, setAccessToken] = useState("");
	const [ip, setIp] = useState("");
	const [publicSettings, setPublicSettings] = useState(null);
	const [userDetails, setUserDetails] = useState(null);
	const [ipLoader, setIpLoader] = useState(true);
	const [isResetPassword, setIsResetPassword] = useState(false);

	const [isEmailVerified, setIsEmailVerified] = useState(false);
	const [isIpBlocked, setIsIpBlocked] = useState(false);

	const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

	const tokenData = localStorage.getItem("token");
	const userData = localStorage.getItem("userData") ? getUserData() : null;

	useEffect(() => {
		const initializeApp = async () => {
			try {
				setIpLoader(true);
				const userIp = await getCurrentUserIpAction();
				setIp(userIp);

				const response = await getPublicSettingsAction();
				setPublicSettings(response.data.data);
			} catch (err) {
				console.error("Initialization error:", err);
			} finally {
				setIpLoader(false);
			}
		};
		initializeApp();
	}, []);

	// Redirect if already logged in
	useEffect(() => {
		if (tokenData && userData) {
			getAuthToken();
			navigate("/dashboard");
		}
	}, [tokenData, navigate, userData]);

	// Snackbar Handlers
	const showSnackbar = (message, severity) => {
		setSnackbar({ open: true, message, severity });
	};

	const handleCloseSnackbar = () => {
		setSnackbar((prev) => ({ ...prev, open: false }));
	};

	// Get Screen Title and Description
	const getScreenContent = () => {
		switch (currentScreen) {
			case SCREENS.FORGOT_PASSWORD:
				return { title: "Forgot Password", description: "We'll send a reset link to your email" };
			case SCREENS.VERIFY_OTP:
				return { title: "Verify OTP", description: "Enter the code sent to your email" };
			case SCREENS.RESET_PASSWORD:
				return { title: "Reset Password", description: "Enter your new password" };
			case SCREENS.CHANGE_PASSWORD:
				return { title: "Change Password", description: "Please update your password" };
			case SCREENS.SET_PASSWORD:
				return { title: "Set Password", description: "Create your new password" };
			default:
				return { title: "Welcome to Master Panel", description: "Sign in to your account" };
		}
	};

	// Common Props for all screens
	const commonProps = {
		email,
		setEmail,
		accessToken,
		setAccessToken,
		ip,
		publicSettings,
		userDetails,
		setUserDetails,
		showSnackbar,
		browser,
		navigate,
		updateUserData,
		isResetPassword,
		setIsResetPassword,
		currentScreen,
		setCurrentScreen,
		SCREENS,
		isEmailVerified,
		setIsEmailVerified,
		setIsIpBlocked,
	};

	// Render Current Screen
	const renderScreen = () => {
		switch (currentScreen) {
			case SCREENS.FORGOT_PASSWORD:
				return <ForgotPassword {...commonProps} />;

			case SCREENS.VERIFY_OTP:
				return <VerifyOtp {...commonProps} />;

			case SCREENS.RESET_PASSWORD:
			case SCREENS.CHANGE_PASSWORD:
			case SCREENS.SET_PASSWORD:
				return <ResetPassword {...commonProps} />;

			default:
				return <Login {...commonProps} />;
		}
	};

	const screenContent = getScreenContent();

	if (ipLoader) {
		return (
			<div className="d-flex justify-content-center">
				<Box className="d-flex justify-content-center align-items-center vh-100">
					<CircularProgress />
				</Box>
			</div>
		);
	}

	if (isIpBlocked) {
		return <IpBlockedPage loading={false} />;
	}

	return (
		<div className="Login-container">
			<div className="container">
				<div className="row flex-center">
					<div className="col-12">
						<div className="login-contain">
							<div className="form-box d-flex flex-column align-items-stretch flex-center rounded-4 w-100 w-md-450px p-6 p-md-12 mt-10 mx-auto flex-column-fluid">
								<div className="login-text text-center">
									<Link className="mb-10 mb-xl-5 me-auto d-block">
										<img
											alt="Logo"
											className="mw-50px mw-xl-75px"
											src={NetcluesBrandLogo}
											width={150}
											height={50}
										/>
									</Link>
									<h1 className="title text-dark h1 mb-2 fs-3x font-weight-bold">
										{screenContent.title}
									</h1>
									<p className="fs-3 login-text-muted-dark mb-8">
										{screenContent.description}
									</p>
								</div>

								<form
									className="form w-100 fv-plugins-bootstrap5 fv-plugins-framework"
									noValidate
									id="kt_sign_in_form"
									onSubmit={(e) => e.preventDefault()}
								>
									{renderScreen()}
								</form>
							</div>

							<div className="text-center mt-10">
								<p className="text-white fs-6 mb-3">
									© {new Date().getFullYear()} Netclues Master. All Rights Reserved.
								</p>
								<p className="text-muted fs-7 d-flex align-items-center justify-content-center">
									Powered by
									<Link
										to="https://netclues.ky"
										target="_blank"
										rel="noopener noreferrer"
										className="ms-2 d-inline-flex align-items-center"
									>
										<img src={netcluesLogo} alt="Netclues" title="Netclues" className="h-20px" />
									</Link>
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={2000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: "top", horizontal: "right" }}
			>
				<Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
					<Typography variant="body1" className="fw-bold">
						{snackbar.message}
					</Typography>
				</Alert>
			</Snackbar>
		</div>
	);
};

export default LoginComponent;